import React, { useMemo, useState, useCallback } from 'react';
import { Text } from '@Sage/hoi-poi-ui';
import { getLiteral } from 'utils/getLiteral';
import { setDefaultSender, deleteSender } from 'services/SenderService';
import { successToast, errorToast } from 'utils/toast';
import MenuHoi from 'components/MenuHoi';
import ConfirmModal from 'components/ConfirmModal';
import { Dots } from 'components/SvgIcons';

const SenderActionsCell = (params) => {
    const { data, senders, onChangeDefault, onDeleteSender, onClickEditSender } = params;
    const [isSettingDefault, setIsSettingDefault] = useState(false);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const handleMakeDefault = useCallback(
        (senderId) => {
            setIsSettingDefault(true);

            setDefaultSender(senderId)
                .then(() => {
                    successToast({
                        text: getLiteral('succes_entityupdatedsuccessfully'),
                    });
                    onChangeDefault?.();
                })
                .catch(() => {
                    errorToast({
                        text: getLiteral('label_failed_update'),
                    });
                })
                .finally(() => {
                    setIsSettingDefault(false);
                });
        },
        [onChangeDefault],
    );

    const openDeleteModal = useCallback(() => {
        setIsDeleteModalOpen(true);
    }, []);

    const closeDeleteModal = useCallback(() => {
        if (isDeleting) return;
        setIsDeleteModalOpen(false);
    }, [isDeleting]);

    const handleConfirmDelete = useCallback(() => {
        if (isDeleting) return;

        setIsDeleting(true);

        deleteSender(data.id)
            .then(() => {
                successToast({
                    text: getLiteral('succes_entitydeletedsuccessfully'),
                });
                closeDeleteModal();
                onDeleteSender?.();
            })
            .catch(() => {
                errorToast({
                    text: getLiteral('error_generalerror'),
                });
            })
            .finally(() => {
                setIsDeleting(false);
            });
    }, [data.id, isDeleting, onDeleteSender, closeDeleteModal]);

    const menuItems = useMemo(() => {
        return [
            {
                hide: data.default,
                icon: 'defaultSender',
                title: <Text>{getLiteral('label_marketing_campaigns_make_default_sender')}</Text>,
                onClick: () => handleMakeDefault(data.id),
                isDisabled: isSettingDefault,
            },
            {
                icon: 'edit',
                title: <Text>{getLiteral('action_edit')}</Text>,
                onClick: () => onClickEditSender?.(data),
            },
            {
                hide: senders.length === 1 || data.default,
                icon: 'delete',
                title: <Text>{getLiteral('action_delete')}</Text>,
                onClick: openDeleteModal,
            },
        ].filter((item) => !item.hide);
    }, [data, senders, handleMakeDefault, isSettingDefault, openDeleteModal, onClickEditSender]);

    return (
        <div className="sender-actions-cell">
            <MenuHoi items={menuItems}>
                <Dots width="18px" height="18px" />
            </MenuHoi>
            <ConfirmModal
                isOpen={isDeleteModalOpen}
                onClose={closeDeleteModal}
                onConfirm={handleConfirmDelete}
                title={getLiteral('title_marketing_campaigns_delete_sender')}
                confirmText={getLiteral('action_delete')}
                cancelText={getLiteral('action_cancel')}
                isLoading={isDeleting}
                buttonType="primary-error"
                size="small"
            >
                <Text>{getLiteral('label_marketing_campaigns_delete_sender_message')}</Text>
            </ConfirmModal>
        </div>
    );
};

SenderActionsCell.displayName = 'SenderActionsCell';

export default SenderActionsCell;
